/**
 * Analytics Hook
 * Provides easy access to PostHog tracking throughout the app
 */

import { useCallback, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { trackEvent, trackPageView } from '../config/posthog';

// Eventos sueltos, sin el efecto de pageview del hook: PostHog ya captura los pageviews
// (capture_pageview: 'history_change'), y llamar useAnalytics() en cada componente los duplicaria.
export const trackVoteViewed = (voteId: string, topic: string): void => {
  trackEvent('vote_viewed', {
    vote_id: voteId,
    topic: topic,
  });
};

export const trackShare = (contentType: string, contentId: string, method: string): void => {
  trackEvent('content_shared', {
    content_type: contentType,
    content_id: contentId,
    share_method: method,
  });
};

/**
 * Hook for tracking analytics events
 */
export const useAnalytics = () => {
  const location = useLocation();

  // Track page views on route changes
  useEffect(() => {
    trackPageView(location.pathname + location.search);
  }, [location]);

  // Track deputy profile view
  const trackDeputyView = useCallback((deputyId: number, deputyName: string, party: string) => {
    trackEvent('deputy_profile_viewed', {
      deputy_id: deputyId,
      deputy_name: deputyName,
      party: party,
    });
  }, []);

  // Track voting record interaction

  // Track search
  const trackSearch = useCallback((query: string, resultCount: number) => {
    trackEvent('search_performed', {
      query: query,
      result_count: resultCount,
    });
  }, []);

  // Track filter applied
  const trackFilterApplied = useCallback((filterType: string, filterValue: string) => {
    trackEvent('filter_applied', {
      filter_type: filterType,
      filter_value: filterValue,
    });
  }, []);

  // Track tab/section change
  const trackSectionViewed = useCallback((sectionName: string, context?: string) => {
    trackEvent('section_viewed', {
      section: sectionName,
      context: context,
    });
  }, []);

  // Track external link click
  const trackExternalLinkClick = useCallback((url: string, linkText: string) => {
    trackEvent('external_link_clicked', {
      url: url,
      link_text: linkText,
    });
  }, []);

  // Track share action

  // Track comparison started
  const trackComparisonStarted = useCallback((deputyIds: number[]) => {
    trackEvent('comparison_started', {
      deputy_count: deputyIds.length,
      deputy_ids: deputyIds,
    });
  }, []);

  // Track topic exploration
  const trackTopicExplored = useCallback((topicName: string, subtopic?: string) => {
    trackEvent('topic_explored', {
      topic: topicName,
      subtopic: subtopic,
    });
  }, []);

  // Generic event tracking
  const track = useCallback((eventName: string, properties?: Record<string, unknown>) => {
    trackEvent(eventName, properties);
  }, []);

  return {
    trackDeputyView,
    trackVoteViewed,
    trackSearch,
    trackFilterApplied,
    trackSectionViewed,
    trackExternalLinkClick,
    trackShare,
    trackComparisonStarted,
    trackTopicExplored,
    track,
  };
};

export default useAnalytics;
